import React, { useEffect, useMemo, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import Select from "react-select";
import api from "../../api";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/common/ConfirmModal";
import Can from "../../components/Can";
import "./AdminSidebarPage.css";

const initialForm = {
  label: "",
  path: "",
  icon: "",
  role_id: "",
  unit_id: "",
  order_no: 0,
  // Admin Sidebar Restructure: optional parent menu — leave blank for a
  // top-level menu item; set to nest this row as a child under another
  // existing menu (see database/admin-sidebar-restructure/
  // 001_add_sidebar_hierarchy_columns.sql).
  parent_id: "",
  // Defaults to visible; uncheck to hide a menu without deleting it.
  is_active: true,
  // Phase 4C: optional permission gate — leave both blank for the exact
  // pre-4C behavior (visible to whoever role_id/unit_id already matched).
  // Set both to additionally require the viewer to hold that permission
  // (e.g. module "form_assignments", action "create" for an Assign Form
  // link) — see PHASE_4C_SIDEBAR_ACCESS_MATRIX.md for the recommended keys.
  required_permission_module: "",
  required_permission_action: "",
};

const SidebarManager = () => {
  const [menus, setMenus] = useState([]);
  const [roles, setRoles] = useState([]);
  const [units, setUnits] = useState([]);
  // Database-driven source for the Required Permission Module/Action
  // pickers -- GET /sidebar/permission-options reuses the exact same
  // app_modules/app_permissions query Access Management's Role/User
  // Permissions pages already use (services/accessManagementService.js's
  // listModulesWithPermissions()), so these dropdowns can never drift from
  // what actually exists and never require typing a raw key.
  const [permissionModules, setPermissionModules] = useState([]);

  const [search, setSearch] = useState("");
  const [filterUnit, setFilterUnit] = useState("");
  const [filterRole, setFilterRole] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterUnit, filterRole, rowsPerPage]);

  const fetchAll = async () => {
    try {
      setLoading(true);

      const [menuRes, roleRes, unitRes, permissionOptionsRes] = await Promise.all([
        api.get(`/sidebar/all`),
        api.get(`/roles`),
        api.get(`/units`),
        api.get(`/sidebar/permission-options`),
      ]);

      setMenus(Array.isArray(menuRes.data) ? menuRes.data : []);
      setRoles(Array.isArray(roleRes.data) ? roleRes.data : []);
      setUnits(Array.isArray(unitRes.data) ? unitRes.data : []);
      setPermissionModules(Array.isArray(permissionOptionsRes.data?.data) ? permissionOptionsRes.data.data : []);
    } catch (error) {
      console.error("Fetch Sidebar Data Error:", error);
      toast.error("Failed to fetch sidebar data");
    } finally {
      setLoading(false);
    }
  };

  const roleMap = useMemo(() => {
    return Object.fromEntries(roles.map((role) => [String(role.id), role.name]));
  }, [roles]);

  const unitMap = useMemo(() => {
    return Object.fromEntries(units.map((unit) => [String(unit.id), unit.name]));
  }, [units]);

  const NO_PERMISSION_VALUE = "";

  const selectedPermissionModule = useMemo(
    () => permissionModules.find((m) => m.moduleKey === form.required_permission_module) || null,
    [permissionModules, form.required_permission_module]
  );

  // A saved module_key/permission_key that no longer exists in app_modules/
  // app_permissions (e.g. the module was deactivated, or the permission was
  // renamed) must stay visibly selected -- never silently cleared -- so the
  // admin can see exactly what's wrong and choose a replacement themselves.
  const moduleIsLegacyMissing = !!form.required_permission_module && !selectedPermissionModule;
  const actionIsLegacyMissing =
    !!form.required_permission_action &&
    (!selectedPermissionModule || !selectedPermissionModule.permissions.some((p) => p.key === form.required_permission_action));

  const moduleOptions = useMemo(() => {
    const options = [
      { value: NO_PERMISSION_VALUE, label: "No permission required" },
      ...permissionModules.map((m) => ({ value: m.moduleKey, label: m.moduleName })),
    ];
    if (moduleIsLegacyMissing) {
      options.push({ value: form.required_permission_module, label: `${form.required_permission_module} (module no longer exists)`, isLegacy: true });
    }
    return options;
  }, [permissionModules, moduleIsLegacyMissing, form.required_permission_module]);

  const actionOptions = useMemo(() => {
    const options = (selectedPermissionModule?.permissions || []).map((p) => ({ value: p.key, label: p.name }));
    if (actionIsLegacyMissing) {
      options.push({ value: form.required_permission_action, label: `${form.required_permission_action} (no longer valid for this module)`, isLegacy: true });
    }
    return options;
  }, [selectedPermissionModule, actionIsLegacyMissing, form.required_permission_action]);

  const selectedModuleOption = moduleOptions.find((o) => o.value === form.required_permission_module) || null;
  const selectedActionOption = actionOptions.find((o) => o.value === form.required_permission_action) || null;

  const handlePermissionModuleChange = (option) => {
    const nextModule = option?.value || "";
    setForm((prev) => ({
      ...prev,
      required_permission_module: nextModule,
      // A permission action only ever means something within its own
      // module -- changing the module always clears the action rather than
      // risking it silently pointing at a different module's permission.
      required_permission_action: "",
    }));
  };

  const handlePermissionActionChange = (option) => {
    handleChange("required_permission_action", option?.value || "");
  };

  const filteredMenus = useMemo(() => {
    return [...menus]
      .sort((a, b) => Number(a.order_no || 0) - Number(b.order_no || 0))
      .filter((menu) =>
        menu.label?.toLowerCase().includes(search.toLowerCase())
      )
      .filter((menu) => (filterUnit ? String(menu.unit_id) === String(filterUnit) : true))
      .filter((menu) => (filterRole ? String(menu.role_id) === String(filterRole) : true));
  }, [menus, search, filterUnit, filterRole]);

  const totalPages = Math.ceil(filteredMenus.length / rowsPerPage);
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const paginatedMenus = filteredMenus.slice(indexOfFirst, indexOfLast);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm({
      ...initialForm,
      order_no: menus.length + 1,
    });
    setShowModal(true);
  };

  const openEditModal = (menu) => {
    setEditingId(menu.id);
    setForm({
      label: menu.label || "",
      path: menu.path || "",
      icon: menu.icon || "",
      role_id: menu.role_id || "",
      unit_id: menu.unit_id || "",
      order_no: menu.order_no || 0,
      parent_id: menu.parent_id || "",
      is_active: menu.is_active === 0 || menu.is_active === false ? false : true,
      required_permission_module: menu.required_permission_module || "",
      required_permission_action: menu.required_permission_action || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const validateForm = () => {
    if (!form.label.trim()) {
      toast.warning("Menu label is required");
      return false;
    }

    if (!form.path.trim()) {
      toast.warning("Menu path is required");
      return false;
    }

    if (editingId && form.parent_id && String(form.parent_id) === String(editingId)) {
      toast.warning("A menu cannot be its own parent");
      return false;
    }

    if (moduleIsLegacyMissing || actionIsLegacyMissing) {
      toast.warning("This menu's Required Permission no longer matches an existing module/action — pick a valid one (or \"No permission required\") before saving.");
      return false;
    }

    return true;
  };

  // Menus a given row is allowed to be nested under — every other menu,
  // minus itself (a menu can never be its own parent).
  const parentOptionsFor = (id) => menus.filter((m) => String(m.id) !== String(id));

  const menuLabelById = useMemo(() => {
    return Object.fromEntries(menus.map((m) => [String(m.id), m.label]));
  }, [menus]);

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = {
        label: form.label.trim(),
        path: form.path.trim(),
        icon: form.icon.trim(),
        role_id: form.role_id || null,
        unit_id: form.unit_id || null,
        order_no: Number(form.order_no || 0),
        parent_id: form.parent_id || null,
        is_active: !!form.is_active,
        required_permission_module: form.required_permission_module.trim() || null,
        required_permission_action: form.required_permission_action.trim() || null,
      };

      if (editingId) {
        await api.put(`/sidebar/${editingId}`, payload);
        toast.success("Menu updated successfully");
      } else {
        await api.post(`/sidebar`, payload);
        toast.success("Menu added successfully");
      }

      closeModal();
      fetchAll();
    } catch (error) {
      console.error("Save Menu Error:", error);
      toast.error(error.response?.data?.message || "Failed to save menu");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/sidebar/${deleteId}`);
      toast.success("Menu deleted successfully");
      closeDeleteModal();
      fetchAll();
    } catch (error) {
      console.error("Delete Menu Error:", error);
      toast.error(error.response?.data?.message || "Failed to delete menu");
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    if (!filteredMenus.length) {
      toast.warning("No sidebar menus available to export");
      return;
    }

    const exportData = filteredMenus.map((menu) => ({
      ID: menu.id,
      Label: menu.label,
      Path: menu.path,
      Icon: menu.icon,
      Parent: menu.parent_id ? (menuLabelById[String(menu.parent_id)] || menu.parent_id) : "Top-level",
      Role: roleMap[String(menu.role_id)] || "All",
      Unit: unitMap[String(menu.unit_id)] || "All",
      Order: menu.order_no,
      Active: menu.is_active === 0 || menu.is_active === false ? "No" : "Yes",
    }));

    const sheet = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, sheet, "Sidebar Menus");
    XLSX.writeFile(wb, "SidebarMenus.xlsx");
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    if (search || filterUnit || filterRole) {
      toast.warning("Please clear search/filter before changing menu order");
      return;
    }

    try {
      const orderedMenus = [...menus].sort(
        (a, b) => Number(a.order_no || 0) - Number(b.order_no || 0)
      );

      const sourceIndex = indexOfFirst + result.source.index;
      const destinationIndex = indexOfFirst + result.destination.index;

      const [movedItem] = orderedMenus.splice(sourceIndex, 1);
      orderedMenus.splice(destinationIndex, 0, movedItem);

      const updatedMenus = orderedMenus.map((item, index) => ({
        ...item,
        order_no: index + 1,
      }));

      setMenus(updatedMenus);

      await Promise.all(
        updatedMenus.map((item) =>
          api.put(`/sidebar/${item.id}`, item)
        )
      );
    } catch (error) {
      console.error("Drag Order Update Error:", error);
      toast.error("Failed to update menu order");
      fetchAll();
    }
  };

  const clearFilters = () => {
    setSearch("");
    setFilterRole("");
    setFilterUnit("");
  };

  return (
    <div className="unit-page sidebar-mgmt-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Configuration</div>
          <h2>Sidebar Management</h2>
          <p>Manage sidebar menus, role-wise visibility, unit access and menu order.</p>
        </div>

        <div className="unit-header-actions">
          <button className="unit-btn unit-btn-light" onClick={fetchAll}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i> Refresh
          </button>

          <button className="unit-btn unit-btn-light" onClick={handleExport}>
            <i className="fa-solid fa-file-export" aria-hidden="true"></i> Export
          </button>

          <Can moduleKey="sidebar" actionKey="create">
            <button className="unit-btn unit-btn-primary" onClick={openAddModal}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i> Add Menu
            </button>
          </Can>
        </div>
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="sidebar-stat-icon sidebar-stat-icon-primary">
            <i className="fa-solid fa-bars" aria-hidden="true"></i>
          </div>
          <div className="sidebar-stat-text">
            <span>Total Menus</span>
            <strong>{menus.length}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="sidebar-stat-icon sidebar-stat-icon-accent">
            <i className="fa-solid fa-filter" aria-hidden="true"></i>
          </div>
          <div className="sidebar-stat-text">
            <span>Filtered Result</span>
            <strong>{filteredMenus.length}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="sidebar-stat-icon sidebar-stat-icon-success">
            <i className="fa-solid fa-file-lines" aria-hidden="true"></i>
          </div>
          <div className="sidebar-stat-text">
            <span>Current Page</span>
            <strong>{currentPage}</strong>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-list sidebar-list-icon" aria-hidden="true"></i>
              Sidebar Menu List
            </h5>
            <p>Drag rows to change order. Clear filters before reordering menus.</p>
          </div>

          <div className="unit-toolbar-actions">
            <div className="input-group unit-search">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </span>

              <input
                className="form-control"
                placeholder="Search menu label..."
                aria-label="Search menu label"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button className="unit-clear-btn" type="button" onClick={() => setSearch("")}>
                  Clear
                </button>
              )}
            </div>

            <select
              className="form-select unit-page-size"
              value={filterUnit}
              onChange={(e) => setFilterUnit(e.target.value)}
            >
              <option value="">All Units</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>

            <select
              className="form-select unit-page-size"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>

            <select
              className="form-select unit-page-size"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={5}>5 rows</option>
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
            </select>

            {(search || filterUnit || filterRole) && (
              <button className="unit-btn unit-btn-light" onClick={clearFilters}>
                Reset
              </button>
            )}
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader">
              <div className="spinner-border" role="status"></div>
              <p>Loading sidebar menus...</p>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="menus">
                {(provided) => (
                  <table
                    className="table unit-table sidebar-drag-table"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <thead>
                      <tr>
                        <th style={{ width: "70px" }}>Order</th>
                        <th>Menu</th>
                        <th>Parent</th>
                        <th>Path</th>
                        <th>Icon</th>
                        <th>Role</th>
                        <th>Unit</th>
                        <th style={{ width: "90px" }}>Active</th>
                        <th className="text-end" style={{ width: "120px" }}>
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedMenus.length > 0 ? (
                        paginatedMenus.map((menu, index) => (
                          <Draggable
                            key={menu.id}
                            draggableId={`${menu.id}`}
                            index={index}
                          >
                            {(provided) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <td>
                                  <span className="unit-id">
                                    <i className="fa-solid fa-grip-vertical sidebar-drag-grip" aria-hidden="true"></i>
                                    #{menu.order_no || indexOfFirst + index + 1}
                                  </span>
                                </td>

                                <td>
                                  <div className="unit-name-box">
                                    <div className="unit-letter">
                                      {menu.label?.charAt(0)?.toUpperCase() || "M"}
                                    </div>

                                    <div>
                                      <strong>{menu.label}</strong>
                                      <small>Menu ID: {menu.id}</small>
                                    </div>
                                  </div>
                                </td>

                                <td>
                                  {menu.parent_id ? (
                                    menuLabelById[String(menu.parent_id)] || `#${menu.parent_id}`
                                  ) : (
                                    <span className="text-muted">Top-level</span>
                                  )}
                                </td>

                                <td>{menu.path || "-"}</td>

                                <td>
                                  {menu.icon ? (
                                    <>
                                      <i className={menu.icon}></i> {menu.icon}
                                    </>
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                <td>
                                  <span className="unit-status">
                                    {roleMap[String(menu.role_id)] || "All"}
                                  </span>
                                </td>

                                <td>{unitMap[String(menu.unit_id)] || "All"}</td>

                                <td>
                                  <span className={`unit-status ${menu.is_active === 0 || menu.is_active === false ? "unit-status-inactive" : "unit-status-active"}`}>
                                    {menu.is_active === 0 || menu.is_active === false ? "Inactive" : "Active"}
                                  </span>
                                </td>

                                <td>
                                  <div className="unit-actions">
                                    <Can moduleKey="sidebar" actionKey="edit">
                                      <button
                                        className="unit-icon-btn unit-edit"
                                        title="Edit"
                                        aria-label={`Edit ${menu.label || "menu"}`}
                                        onClick={() => openEditModal(menu)}
                                      >
                                        <i className="fa-solid fa-pen" aria-hidden="true"></i>
                                      </button>
                                    </Can>

                                    <Can moduleKey="sidebar" actionKey="delete">
                                      <button
                                        className="unit-icon-btn unit-delete"
                                        title="Delete"
                                        aria-label={`Delete ${menu.label || "menu"}`}
                                        onClick={() => openDeleteModal(menu.id)}
                                      >
                                        <i className="fa-solid fa-trash" aria-hidden="true"></i>
                                      </button>
                                    </Can>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9">
                            <div className="unit-empty">
                              <div className="unit-empty-icon">
                                <i className="fa-solid fa-bars" aria-hidden="true"></i>
                              </div>
                              <h5>No sidebar menus found</h5>
                              <p>Try another filter or add a new sidebar menu.</p>

                              <button
                                className="unit-btn unit-btn-primary"
                                onClick={openAddModal}
                              >
                                <i className="fa-solid fa-plus" aria-hidden="true"></i> Add Menu
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {provided.placeholder}
                    </tbody>
                  </table>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing{" "}
            <strong>{filteredMenus.length === 0 ? 0 : indexOfFirst + 1}</strong>{" "}
            to <strong>{Math.min(indexOfLast, filteredMenus.length)}</strong>{" "}
            of <strong>{filteredMenus.length}</strong> entries
          </div>

          {totalPages > 1 && (
            <ul className="pagination unit-pagination">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
              </li>

              {compactPageItems([...Array(totalPages)].map((_, index) => (
                <li
                  key={index}
                  className={`page-item ${
                    currentPage === index + 1 ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              )), currentPage)}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      <div className="unit-card mt-4">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-eye sidebar-list-icon" aria-hidden="true"></i>
              Sidebar Preview
            </h5>
            <p>Quick preview of filtered sidebar menus.</p>
          </div>
        </div>

        <div className="unit-table-wrapper">
          <table className="table unit-table sidebar-preview-table">
            <thead>
              <tr>
                <th>Menu</th>
                <th>Path</th>
                <th>Role</th>
                <th>Unit</th>
              </tr>
            </thead>

            <tbody>
              {filteredMenus.length > 0 ? (
                filteredMenus.map((menu) => (
                  <tr key={`preview-${menu.id}`}>
                    <td>
                      <div className="unit-name-box">
                        <div className="unit-letter">
                          {menu.label?.charAt(0)?.toUpperCase() || "M"}
                        </div>

                        <div>
                          <strong>{menu.label}</strong>
                          <small>Order: {menu.order_no || "-"}</small>
                        </div>
                      </div>
                    </td>
                    <td>{menu.path || "-"}</td>
                    <td>{roleMap[String(menu.role_id)] || "All"}</td>
                    <td>{unitMap[String(menu.unit_id)] || "All"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <div className="unit-empty">
                      <h5>No preview available</h5>
                      <p>No menu matched your current filters.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div
          className="modal show fade d-block unit-modal"
          tabIndex="-1"
          style={{ background: "rgba(15, 23, 42, 0.55)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingId ? "Edit Sidebar Menu" : "Add Sidebar Menu"}
                </h5>

                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Menu Label</label>
                    <input
                      className="form-control"
                      placeholder="Example: Dashboard"
                      value={form.label}
                      onChange={(e) => handleChange("label", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Path</label>
                    <input
                      className="form-control"
                      placeholder="Example: /admin/dashboard"
                      value={form.path}
                      onChange={(e) => handleChange("path", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Icon Class</label>
                    <input
                      className="form-control"
                      placeholder="Example: fa fa-home"
                      value={form.icon}
                      onChange={(e) => handleChange("icon", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Order No</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.order_no}
                      onChange={(e) => handleChange("order_no", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Parent Module</label>
                    <select
                      className="form-select"
                      value={form.parent_id}
                      onChange={(e) => handleChange("parent_id", e.target.value)}
                    >
                      <option value="">None — Top-level menu</option>
                      {parentOptionsFor(editingId).map((menu) => (
                        <option key={menu.id} value={menu.id}>
                          {menu.label}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Leave as "None" for a top-level module; pick an existing menu to nest this as its child.</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Active Status</label>
                    <div className="form-check form-switch pt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="sidebarMenuActive"
                        checked={form.is_active}
                        onChange={(e) => handleChange("is_active", e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="sidebarMenuActive">
                        {form.is_active ? "Active (visible in sidebar)" : "Inactive (hidden from sidebar)"}
                      </label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={form.role_id}
                      onChange={(e) => handleChange("role_id", e.target.value)}
                    >
                      <option value="">All Roles</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Unit</label>
                    <select
                      className="form-select"
                      value={form.unit_id}
                      onChange={(e) => handleChange("unit_id", e.target.value)}
                    >
                      <option value="">All Units</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <hr className="my-1" />
                    <label className="form-label mb-0"><strong>Required Permission (optional)</strong></label>
                    <small className="text-muted d-block">
                      Choose "No permission required" for a menu that should stay role/unit-only, exactly as before —
                      or pick a module and action to additionally gate this menu on that permission.
                    </small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Required Permission Module</label>
                    <Select
                      classNamePrefix="sidebar-permission-select"
                      options={moduleOptions}
                      value={selectedModuleOption}
                      onChange={handlePermissionModuleChange}
                      isClearable={false}
                      placeholder="Search modules..."
                      noOptionsMessage={() => "No modules found"}
                    />
                    {moduleIsLegacyMissing && (
                      <small className="text-danger d-block mt-1">
                        ⚠ "{form.required_permission_module}" no longer exists in app_modules — select a valid module (or "No permission required") before saving.
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Required Permission Action</label>
                    <Select
                      classNamePrefix="sidebar-permission-select"
                      options={actionOptions}
                      value={selectedActionOption}
                      onChange={handlePermissionActionChange}
                      isClearable={false}
                      isDisabled={!form.required_permission_module}
                      placeholder={form.required_permission_module ? "Search actions..." : "Select a module first"}
                      noOptionsMessage={() => "No actions found for this module"}
                    />
                    {actionIsLegacyMissing && (
                      <small className="text-danger d-block mt-1">
                        ⚠ "{form.required_permission_action}" is no longer a valid action for this module — select a valid action before saving.
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="unit-btn unit-btn-light" onClick={closeModal}>
                  Cancel
                </button>

                <button
                  className="unit-btn unit-btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : editingId ? "Update Menu" : "Create Menu"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showDeleteModal}
        onHide={closeDeleteModal}
        onConfirm={confirmDelete}
        busy={deleting}
        title="Delete Sidebar Menu?"
        message="Are you sure you want to delete this sidebar menu? This action cannot be undone."
        confirmLabel="Delete"
        dialogClassName="admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
      />
    </div>
  );
};

export default SidebarManager;